import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { CompanyService } from '../company/company.service';
import { ConfigService } from '../config/config.service';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const companyService = inject(CompanyService);
  const config = inject(ConfigService);

  if (req.url.startsWith(config.keycloak().url)) {
    return next(req);
  }

  // Token wychodzi tylko do naszego API.
  if (!isApiRequest(req.url, config.apiUrl())) {
    return next(req);
  }

  const authedReq = attachHeaders(req, authService, companyService);

  return next(authedReq).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return authService.refreshAccessToken().pipe(
          switchMap(() => next(attachHeaders(req, authService, companyService))),
          catchError(() => {
            authService.logout();
            return throwError(() => err);
          }),
        );
      }
      return throwError(() => err);
    }),
  );
};

function attachHeaders(
  req: HttpRequest<unknown>,
  authService: AuthService,
  companyService: CompanyService,
): HttpRequest<unknown> {
  const token = authService.accessToken();
  const companyId = companyService.selectedCompanyId();

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (companyId) headers['company-id'] = companyId;

  return req.clone({ setHeaders: headers });
}

// isApiRequest odpowiada na jedno pytanie: czy to nasze API.
//
// Interceptor doklejał nagłówek Authorization do KAŻDEGO żądania wychodzącego
// z aplikacji. Wystarczyło jedno wywołanie do obcej domeny — obrazek, mapa,
// zewnętrzne API, adres pochodzący z danych — żeby token dostępowy użytkownika
// trafił do cudzego serwera i pozwolił działać w jego imieniu. Token jedzie
// wyłącznie pod adres bramy.
//
// Adres bramy bierzemy z konfiguracji uruchomieniowej (`ConfigService`), a nie ze
// stałej z `environment`. Instalacja kasy polega na wpisaniu adresu API w ustawieniach,
// więc wartość z builda jest po prostu nieaktualna: porównanie z nią odrzucałoby
// wszystkie żądania do prawdziwej bramy i nagłówki nigdy by się nie dokleiły.
function isApiRequest(url: string, apiBase: string): boolean {
  if (url.startsWith('/')) {
    return true; // adres względny — ten sam origin co aplikacja
  }
  try {
    return new URL(url, document.baseURI).origin === new URL(apiBase, document.baseURI).origin;
  } catch {
    return false;
  }
}
